import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import GlassBoxBadge from './GlassBoxBadge';

// Deterministic pseudo-random from project id string
function seedRandom(id) {
  let hash = 0;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  const x = Math.sin(Math.abs(hash) * 9301 + 49297) * 49297;
  return Math.abs(x - Math.floor(x));
}

const avatarColors = ['#7C3AED', '#2563EB', '#10B981', '#EC4899', '#F59E0B', '#06B6D4', '#8B5CF6', '#EF4444'];

function DonorAvatarStack({ projectId }) {
  const donorCount = Math.floor(seedRandom(projectId) * 145) + 5;
  const count = Math.min(4, donorCount);
  const initials = ['SC', 'MR', 'JO', 'DP', 'EW', 'JW', 'AR', 'MS'];
  let hash = 0;
  for (let i = 0; i < String(projectId).length; i++) hash = ((hash << 5) - hash) + String(projectId).charCodeAt(i);

  return (
    <div className="flex items-center gap-1.5" data-testid={`donor-proof-${projectId}`}>
      <div className="flex -space-x-1.5">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className="w-5 h-5 rounded-full border-[1.5px] border-white flex items-center justify-center text-white text-[7px] font-bold"
            style={{ backgroundColor: avatarColors[Math.abs(hash * 3 + i) % avatarColors.length] }}
          >
            {initials[Math.abs(hash * 3 + i) % initials.length]}
          </div>
        ))}
      </div>
      <span className="text-[11px] text-gray-400 font-body">{donorCount} donors</span>
    </div>
  );
}

export function ProjectCard({ project }) {
  const percent = Math.round((project.fundingRaised / project.fundingGoal) * 100);

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-1 group"
      data-testid={`project-card-${project.slug}`}
    >
      <div className="relative overflow-hidden h-48">
        <img
          src={project.heroImage}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 right-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            project.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
            project.status === 'nearly-funded' ? 'bg-blue-100 text-blue-700' :
            project.status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
            'bg-gray-100 text-gray-600'
          }`} data-testid={`project-status-${project.slug}`}>
            {project.status === 'just-started' ? 'Just Started' :
             project.status === 'in-progress' ? 'In Progress' :
             project.status === 'nearly-funded' ? 'Nearly Funded' : 'Complete'}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {project.ngoSlug ? (
            <Link to={`/ngo/${project.ngoSlug}`} className="text-sm text-gray-500 hover:text-[#7C3AED] transition-colors font-body" data-testid={`ngo-link-${project.slug}`}>
              {project.ngoName}
            </Link>
          ) : (
            <span className="text-sm text-gray-500 font-body">{project.ngoName}</span>
          )}
          <GlassBoxBadge txHash={project.txHash} />
        </div>
        <h3 className="font-heading font-semibold text-[#111827] text-lg mb-2 line-clamp-2">
          {project.title}
        </h3>
        <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed font-body">
          {project.description?.split('\n')[0]}
        </p>
        <div className="mb-3">
          <div className="flex justify-between text-sm mb-1.5">
            <span className="font-medium text-[#111827]">
              ${project.fundingRaised?.toLocaleString()}
            </span>
            <span className="text-gray-400">
              of ${project.fundingGoal?.toLocaleString()}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] rounded-full transition-all duration-700"
              style={{ width: `${Math.min(percent, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <DonorAvatarStack projectId={project.id} />
          <Link
            to={`/project/${project.slug}`}
            className="text-sm font-medium text-[#7C3AED] hover:text-[#2563EB] transition-colors"
            data-testid={`view-project-${project.slug}`}
          >
            View Project
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ProjectCard;
